import React, { useState, useMemo, useEffect } from "react";
import "./chatRelationsDataTable.scss";
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableToolbar,
  TableToolbarSearch,
  DataTableSkeleton,
  Pagination,
  Button,
  Tooltip,
  OverflowMenu,
  OverflowMenuItem,
  ContentSwitcher,
  Switch,
  ContainedList,
  Search,
  ContainedListItem,
} from "@carbon/react";
import { UserFollow } from "@carbon/icons-react";
import { queryClient } from "../..";
import { useNavigate } from "react-router-dom";
import { saveRoute, clearRoute } from "../../store/routeStorage";
import { ReactComponent as FluffyChat } from "../../assets/icons/fluffyChatIcon.svg";
import { chatRelationsMapper } from "../../mappers/chatRelationsMapper";
import { useTranslation } from "react-i18next";
import TopUpFuelModal from "../topUpFuelModal/TopUpFuelModal";
import ClientDepositModal from "../clientDepositModal/ClientDepositModal";
import DeleteItemModal from "../deleteItemModal/DeleteItemModal";
import { useAddClientSettings } from "../../hooks/useAddClientSettings";

export default function ChatRelationsDataTable({
  relations,
  pending,
  setOpenSubagentCreationModal,
  setOpenAddNewClientModal,
  showSuccessToast,
}) {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "name", direction: "ASC" });
  const [openTopUpFuelModal, setOpenTopUpFuelModal] = useState(false);
  const [editingFuelClient, setEditingFuelClient] = useState(null);
  const [openDepositEditing, setOpenDepositEditing] = useState(false);
  const [editingPriceClient, setEditingPriceClient] = useState(null);
  const [openClientRemoval, setOpenClientRemoval] = useState(false);
  const [removingClient, setRemovingClient] = useState(null);
  // const [updatingAutoapproveGuid, setUpdatingAutoapproveGuid] = useState(null);
  const [autoapproveState, setAutoapproveState] = useState(() =>
    Object.fromEntries(
      (relations || []).map((item) => [item.guid, item.autoapprove]),
    ),
  );

  const navigate = useNavigate();

  const BALANCE_VIEW_KEY = "balance_view";

  const getBalanceView = () => localStorage.getItem(BALANCE_VIEW_KEY) || "fuel";

  const [balanceView, setBalanceView] = useState(getBalanceView);

  const { t, i18n } = useTranslation();

  const settingsMutation = useAddClientSettings();

  const handleChangeAutoapproveSettings = (checked, item) => {
    settingsMutation.mutate(
      { guid: item.guid, autoapprove: checked, cost: Number(item.cost ?? 0) },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["getInfo"] });
        },
        onError: (error) => {
          clearRoute("admin");
          navigate("/error", {
            state: { code: error.code, message: error.message },
          });
        },
      },
    );
  };

  useEffect(() => {
    localStorage.setItem(BALANCE_VIEW_KEY, balanceView);
  }, [balanceView]);

  const handleFuelEdit = (item) => {
    setEditingFuelClient({
      guid: item.guid,
      client: item.client,
      fuel_balance: item.fuel_balance,
      balance: item.balance,
      cost: item.cost,
    });
    setOpenTopUpFuelModal(true);
  };

  const handlePriceEdit = (item) => {
    setEditingPriceClient({
      guid: item.guid,
      client: item.client,
      fuel_balance: item.fuel_balance,
      balance: item.balance,
      discount: item.discount,
      cost: item.cost,
      autoapprove: item.autoapprove,
    });
    setOpenDepositEditing(true);
  };

  const handleDelete = (item) => {
    setRemovingClient({ guid: item.guid, client: item.client });
    setOpenClientRemoval(true);
  };

  const { headers: mappedHeaders, rows: mappedRows } = chatRelationsMapper(
    relations,
    t,
    i18n,
    {
      onFuelEdit: handleFuelEdit,
      onPriceEdit: handlePriceEdit,
      onDelete: handleDelete,
      balanceView,
      onAutoapproveEdit: handleChangeAutoapproveSettings,
      settingsMutation,
    },
  );

  const extractText = (val) => {
    if (val == null) return "";

    if (typeof val === "string" || typeof val === "number") {
      return String(val);
    }

    if (React.isValidElement(val)) {
      if (typeof val.props?.toggled === "boolean") {
        return val.props.toggled
          ? t("main.chat.chatClientsTable.autoapproveOn")
          : t("main.chat.chatClientsTable.autoapproveOff");
      }

      return extractText(val.props?.children);
    }

    if (Array.isArray(val)) {
      return val.map(extractText).join(" ");
    }

    return "";
  };

  const parseValue = (val) => {
    const raw = extractText(val).trim();

    if (raw === "" || raw === "-") {
      return { type: "empty", value: null };
    }

    // Для toggle: off < on
    if (raw === "off") {
      return { type: "toggle", value: 0 };
    }

    if (raw === "on") {
      return { type: "toggle", value: 1 };
    }

    // Число
    const number = Number(raw);
    if (!Number.isNaN(number)) {
      return { type: "number", value: number };
    }

    // Дата
    const date = Date.parse(raw);
    if (!Number.isNaN(date)) {
      return { type: "date", value: date };
    }

    // Строка
    return { type: "string", value: raw.toLowerCase() };
  };

  const compare = (aRaw, bRaw) => {
    const a = parseValue(aRaw);
    const b = parseValue(bRaw);

    if (a.type === b.type) {
      if (a.type === "empty") return 0;
      if (a.value === b.value) return 0;
      return a.value > b.value ? 1 : -1;
    }

    const priority = {
      empty: 1,
      toggle: 2,
      number: 3,
      date: 4,
      string: 5,
    };

    return priority[a.type] - priority[b.type];
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return mappedRows;

    const normalizedSearch = search.toLowerCase();

    return mappedRows.filter((row) =>
      Object.values(row).some((value) =>
        extractText(value).toLowerCase().includes(normalizedSearch),
      ),
    );
  }, [mappedRows, search]);

  const sortedRows = useMemo(() => {
    if (!sort.key || sort.direction === "NONE") {
      return filteredRows;
    }

    const sorted = [...filteredRows].sort((a, b) => {
      const result = compare(a[sort.key], b[sort.key]);
      return sort.direction === "ASC" ? result : -result;
    });

    return sorted;
  }, [filteredRows, sort]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return sortedRows.slice(start, end);
  }, [sortedRows, pagination]);

  const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(window.matchMedia(query).matches);

    useEffect(() => {
      const media = window.matchMedia(query);
      const listener = () => setMatches(media.matches);

      media.addEventListener("change", listener);
      return () => media.removeEventListener("change", listener);
    }, [query]);

    return matches;
  };

  const isMobile = useMediaQuery("(max-width: 750px)");

  return (
    <div className="main_relations">
      {isMobile ? (
        <ContainedList
          label={t("main.chat.chatClientsTable.title")}
          kind="on-page"
          action={
            <div className="main_relations_mobile_title">
              <OverflowMenu aria-label="overflow-menu" flipped>
                <OverflowMenuItem
                  itemText={t("main.admin.create_subagent.title")}
                  onClick={() => setOpenSubagentCreationModal(true)}
                />
                <OverflowMenuItem
                  itemText={`${t("main.chat.chatClientsTable.mobileBalance")} ${t("main.chat.chatClientsTable.balanceFuel")}`}
                  onClick={() => setBalanceView("fuel")}
                />
                <OverflowMenuItem
                  itemText={`${t("main.chat.chatClientsTable.mobileBalance")} ${t("main.chat.chatClientsTable.balanceUSD")}`}
                  onClick={() => setBalanceView("usd")}
                />
              </OverflowMenu>
              <Button
                className="bind_farmer"
                onClick={() => setOpenAddNewClientModal(true)}
                hasIconOnly
                size="md"
                renderIcon={UserFollow}
                iconDescription={t("main.chat.addButton")}
              ></Button>
            </div>
          }
        >
          <Search
            placeholder={`${t("main.chat.chatClientsTable.mobileLisSearch")}`}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            closeButtonLabelText={`${t("main.searchText.closeButtonText")}`}
            size="lg"
            labelText="Filter search"
          />

          {filteredRows.map((row, key) => (
            <ContainedListItem key={key}>
              <div className="main_relations_mobile_list_item">
                <div className="main_relations_mobile_list_item_info">
                  <span>{row.kind}</span>
                  <span>{row.guid}</span>
                  <span>{row.client}</span>
                  <span className="fuel_info">{row.fuel_balance}</span>
                </div>
                <div className="main_relations_mobile_list_item_buttons">
                  {row.actions}
                </div>
              </div>
            </ContainedListItem>
          ))}
        </ContainedList>
      ) : pending ? (
        <DataTableSkeleton
          aria-label="sample table"
          headers={[
            {
              header: t("main.chat.chatClientsTable.kind"),
              key: "kind",
            },
            {
              header: t("main.chat.chatClientsTable.guid"),
              key: "guid",
            },
            {
              header: t("main.chat.chatClientsTable.client"),
              key: "client",
            },
            {
              header: t("main.chat.chatClientsTable.created"),
              key: "created",
            },
            {
              header: t("main.chat.chatClientsTable.fuel_balance"),
              key: "fuel_balance",
            },
          ]}
          showHeader
          showToolbar
        />
      ) : (
        <DataTable
          size="lg"
          headers={mappedHeaders}
          rows={paginatedRows}
          isSortable
          render={({ rows, headers, getHeaderProps }) => (
            <TableContainer
              title={
                <div className="main_relations_header">
                  <span>{t("main.chat.chatClientsTable.title")}</span>
                  <div className="main_relations_button_set">
                    <Button
                      kind="tertiary"
                      className="create_subagent"
                      onClick={() => setOpenSubagentCreationModal(true)}
                    >
                      {t("main.admin.create_subagent.title")}
                    </Button>

                    <Button
                      className="bind_farmer"
                      onClick={() => setOpenAddNewClientModal(true)}
                      renderIcon={UserFollow}
                    >
                      {t("main.chat.addButton")}
                    </Button>
                  </div>
                </div>
              }
            >
              <TableToolbar className="main_relations_toolbar">
                <TableToolbarSearch
                  value={search}
                  defaultExpanded={!!search}
                  persistent={!!search}
                  placeholder={`${t("main.tableText.placeholder")}`}
                  closeButtonLabelText={`${t(
                    "main.searchText.closeButtonText",
                  )}`}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                />
                <div className="balance_switcher">
                  <p className="balance_switcher_text">
                    {t("main.chat.chatClientsTable.balanceSwitcherText")}
                  </p>
                  <ContentSwitcher
                    selectedIndex={balanceView === "fuel" ? 0 : 1}
                    onChange={({ name }) => setBalanceView(name)}
                    lowContrast
                    className="balance_switcher_content_switcher"
                  >
                    <Switch
                      name="fuel"
                      text={t("main.chat.chatClientsTable.balanceFuel")}
                    />
                    <Switch
                      name="usd"
                      text={t("main.chat.chatClientsTable.balanceUSD")}
                    />
                  </ContentSwitcher>
                </div>
              </TableToolbar>
              <Table>
                <TableHead color="#fff">
                  <TableRow>
                    {headers.map((header) => {
                      const isSorted = sort.key === header.key;

                      const { key, ...headerProps } = getHeaderProps({
                        header,
                        onClick: () => {
                          let nextDirection;

                          if (sort.key !== header.key) {
                            nextDirection = "ASC";
                          } else if (sort.direction === "ASC") {
                            nextDirection = "DESC";
                          } else if (sort.direction === "DESC") {
                            nextDirection = "NONE";
                          } else {
                            nextDirection = "ASC";
                          }

                          setSort((prev) => ({
                            ...prev,
                            key: header.key,
                            direction: nextDirection,
                          }));
                        },
                      });
                      return (
                        <TableHeader
                          key={key}
                          {...headerProps}
                          isSortHeader={isSorted && sort.direction !== "NONE"}
                          sortDirection={isSorted ? sort.direction : "NONE"}
                        >
                          {header.header}
                        </TableHeader>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                backwardText={`${t("main.tableText.pagination.backwardText")}`}
                forwardText={`${t("main.tableText.pagination.forwardText")}`}
                itemsPerPageText={`${t(
                  "main.tableText.pagination.itemsPerPageText",
                )}`}
                page={pagination.page}
                onChange={({ page, pageSize }) => {
                  setPagination((prev) => ({
                    ...prev,
                    page: page,
                    pageSize: pageSize,
                  }));
                }}
                pageNumberText={`${t(
                  "main.tableText.pagination.pageNumberText",
                )}`}
                itemRangeText={(min, max, total) =>
                  `${min}–${max} ${t(
                    "main.tableText.pagination.range",
                  )} ${total} ${
                    total === 1
                      ? t("main.tableText.pagination.itemRangeText.item")
                      : t("main.tableText.pagination.itemRangeText.items")
                  }`
                }
                pageRangeText={(current, total) =>
                  `${t("main.tableText.pagination.range")} ${total} ${
                    total === 1
                      ? t("main.tableText.pagination.pageRangeText.page")
                      : t("main.tableText.pagination.pageRangeText.pages")
                  }`
                }
                pageSize={pagination.pageSize}
                pageSizes={[5, 10, 15]}
                size="md"
                totalItems={filteredRows.length}
              />
            </TableContainer>
          )}
        />
      )}

      {openTopUpFuelModal && (
        <TopUpFuelModal
          openModal={openTopUpFuelModal}
          setOpenModal={setOpenTopUpFuelModal}
          editingClient={editingFuelClient}
          onSuccess={() =>
            showSuccessToast(
              t("authentication.notification.success.title"),
              t("main.admin.top_up_fuel.topped_up"),
            )
          }
        />
      )}
      {openDepositEditing && (
        <ClientDepositModal
          open={openDepositEditing}
          setOpen={setOpenDepositEditing}
          editingClient={editingPriceClient}
          setEditingClient={setEditingPriceClient}
        />
      )}
      {openClientRemoval && (
        <DeleteItemModal
          open={openClientRemoval}
          setOpen={setOpenClientRemoval}
          type="client"
          data={removingClient}
        />
      )}
    </div>
  );
}
